import axios from 'axios';
import { NextRouter } from 'next/router';
import { DOMAINS, JSON_URL } from '../constants/environment';

// collect all helper functions here
export type DATA_CENTER_TYPE = 'US' | 'EU';
type DATA_CENTER_CODE_TYPE = {
  [key: string]: DATA_CENTER_TYPE;
};
type DATA_CENTER_LOCATION_TYPE = {
  [key: string]: string;
};
const DATA_CENTER_LOCATION: DATA_CENTER_CODE_TYPE = {
  AF: 'EU', // Africa
  AN: 'US', // Antarctica
  AS: 'US', // Asia
  AU: 'US', // Australia
  EU: 'EU', // Europe
  NA: 'US', // North America
  OC: 'US', // Oceania
  SA: 'US', // South America
  US: 'US', // United States
};
const DATA_CENTER_NAME: DATA_CENTER_LOCATION_TYPE = {
  US: 'United States',
  EU: 'Europe',
};

export const findTheNearestDCCode = (continentCode = 'NA'): DATA_CENTER_TYPE => DATA_CENTER_LOCATION[continentCode] || DATA_CENTER_LOCATION.US;

export const findTheNearestDCName = (continentCode = 'NA'): string => DATA_CENTER_NAME[DATA_CENTER_LOCATION[continentCode]] || DATA_CENTER_NAME.US;

/**
 * description: for client side redirection
 * @returns the nearest data center url or an empty string
 */
export const getRedirectUrl = async (): Promise<string> => {
  try {
    const isStaging = window.location.href.includes('staging');
    const url = `${isStaging ? JSON_URL.STAGING : JSON_URL.PROD}/sessions?_action=getSessionInfo`;

    return await axios({
      method: 'post',
      url,
      withCredentials: true,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    }).then((response) => {
      if (response.status === 200) {
        return isStaging ? DOMAINS.STAGING : DOMAINS.PROD;
      }
      return '';
    });
  } catch (e) {
    return '';
  }
};

/**
 * description: strips out parameters that may contain PII from the url for server side
 * @returns false or the redirect object for getServerSideProps
 * eg.
 * /sign-up =>  /sign-up
 * /sign-up?gclid=xyz =>  /sign-up?gclid=xyz
 * /sign-up?email=abc@gmail.com&password=123 => /sign-up
 * /sign-up?email=abc@gmail.com&gclid=xyz => /sign-up?gclid=xyz
 */
const ignoredParams = ['email', 'username', 'password'];
export const getRedirectAttribute = (query: any, pageUrl = '/') => {
  // lowercase all params to avoid case sensitivity issues
  const filteredQuery = Object.fromEntries(
    Object.entries(query).map(([k, v]) => {
      if (ignoredParams.includes(k.toLowerCase())) {
        return [k.toLowerCase(), v];
      }
      return [k, v];
    }),
  );

  let shouldRedirect = false;
  ignoredParams.forEach((param: string) => {
    if (filteredQuery[param]) {
      delete filteredQuery[param];
      shouldRedirect = true;
    }
  });

  if (shouldRedirect) {
    const queryString = Object.keys(filteredQuery)
      .map((key) => `${key}=${query[key]}`)
      .join('&');

    return {
      permanent: false,
      destination: queryString ? `?${queryString}` : pageUrl,
    };
  }

  return false;
};

export const getNextPageUrl = function getNextPageUrl(router: NextRouter): string | null {
  const { redirect_to: redirectTo, interval, blocks, product, projects } = router.query;
  if (!redirectTo) return null;
  let query = '';
  if (interval && blocks) query += `interval=${interval}&blocks=${blocks}`;
  if (projects) query += `&projects=${projects}`;
  if (product) query += `&product=${product}`;
  if (query) query = `?${query}`;
  return encodeURIComponent(`${redirectTo}${query}`);
};
